import { clamp } from 'lodash-es';
import { animate } from 'motion';
import { UAParser } from 'ua-parser-js';
import { BUILD_CONFIG, BuildConfig, Platform, detectEnv } from '../assets/src/BuildConfig';
import { Result } from '../assets/src/utils/Result';
import { getObjectKeys } from '../assets/src/utils/collection/get-object-keys';
import { isDesktop } from '../assets/src/utils/device/is-desktop';
import { DEBUG_STORE } from '../assets/src/utils/local-storage-utils';
import { detectPlatform } from '../assets/src/utils/platform/platform';
import { CrazyGamesPlatformPreloader } from './platform/CrazyGamesPlatformPreloader';
import { FbPlatformPreloader } from './platform/FbPlatformPreloader';
import { IPlatformPreloader } from './platform/IPlatformPreloader';
import { VkPlatformPreloader } from './platform/VkPlatformPreloader';
import { YaPlatformPreloader } from './platform/YaPlatformPreloader';

const LOADING_STAGES = {
	'cocos-initial': 20,
	'preloader-onload': 30,
	'device-features': 33,
	'auth': 35,
	'state': 40,
	'platform': 45,
	'resources': 50,
	'time-manager-ready': 65,
	'globalScene': 80,
	'gameScene': 100,
} as const;

export type LoadingStage = keyof typeof LOADING_STAGES;

type HideOptions = { instant: true } | { instant: false; duration?: number };

export class Preloader {
	private preloaderClicksNum = 0;
	private preloaderContainer: HTMLDivElement;
	private loadingText: HTMLDivElement;
	private loadingBar: HTMLSpanElement;

	private isLoadComplete = false;
	private totalProgress = 0;
	private currentStage: LoadingStage = 'cocos-initial';
	private currentStageProgress = 0;
	private intervalId: number;
	private loadComplete = new AbortController();

	private readonly _platformPreloader: IPlatformPreloader | undefined;

	constructor() {
		this.trackPageVisibility(this.loadComplete.signal);

		if (DEBUG_STORE.get('clearConsoleOnStart') === '1') {
			console.clear();
		}

		// @ts-expect-error
		window['userAgent'] = new UAParser();

		BUILD_CONFIG.platform = detectPlatform();
		BUILD_CONFIG.env = detectEnv(location.hostname, BUILD_CONFIG.platform);

		const cocosToolbar = document.querySelector<HTMLDivElement>('.toolbar');
		if (cocosToolbar) {
			// скрываем тулбар от Cocos
			cocosToolbar.style.display = 'none';
		}

		if (BUILD_CONFIG.env === 'personal') {
			this.preventVConsole();
		}

		this.preloaderContainer = document.getElementById('preloader-container') as HTMLDivElement;
		this.loadingText = document.getElementById('loading-progress-text') as HTMLDivElement;
		this.loadingBar = document.getElementById('preloader-bar') as HTMLDivElement;

		this.adjustPreloaderGameLogo();

		if (userAgent.getOS().name === 'iOS' || userAgent.getBrowser().name === 'Safari') {
			document.getElementById('progressbarFrame').style.display = 'none';
		}

		this.addConsoleErrorHook();

		this.adjustScaling();

		this.initBuildInfoText(BUILD_CONFIG);

		this.updateTotalProgress(0);
		this.startStage('cocos-initial');
		this.startFakeProgress();

		if (BUILD_CONFIG.env !== 'production') {
			this.preloaderContainer.addEventListener('pointerdown', this.onPreloaderClick.bind(this));
		}

		this._platformPreloader = this.createPlatformPreloader(BUILD_CONFIG.platform);
		if (this._platformPreloader) {
			this._platformPreloader.loadAndInit().then((result: Result<string>) => {
				this.adjustPreloaderGameLogo();
				console.log(`platform preloader load with result`, result);
			});
		}
	}

	// отслеживаем теряла ли вкладка с игрой фокус во время загрузки, эта информация будет использована позже в аналитике
	private trackPageVisibility(abortSignal: AbortSignal): void {
		if (document.visibilityState === 'hidden') {
			this.onPageHidden();
			return;
		}

		document.addEventListener('visibilitychange', this.onVisibilityChange.bind(this), {
			signal: abortSignal,
		});
	}

	private onVisibilityChange(): void {
		if (document.visibilityState === 'hidden') {
			this.onPageHidden();
		}
	}

	private onPageHidden(): void {
		DEBUG_STORE.set('loadPageWasHidden', '1');
	}

	private adjustPreloaderGameLogo(): void {
		const logo = document.getElementById('preloader-logo');
		if (!logo) {
			return;
		}

		if (BUILD_CONFIG.platform === 'fbInstant' || BUILD_CONFIG.platform === 'cg') {
			logo.classList.add('preloader-logo-en');
		} else if (BUILD_CONFIG.platform === 'ya') {
			if ('yandexGames' in window) {
				// после того как yandex sdk инициализировано мы можем получить локаль игрока и отобразить соответствующее лого
				const lang = yandexGames.environment.i18n.lang;
				console.log(`yandex language: ${lang}`);
				logo.classList.remove('preloader-logo-hidden');
				logo.classList.add(lang === 'ru' ? 'preloader-logo-ru' : 'preloader-logo-en');
			} else {
				logo.classList.add('preloader-logo-hidden');
			}
		} else {
			logo.classList.add('preloader-logo-ru');
		}
	}

	public get platformPreloader(): IPlatformPreloader | undefined {
		return this._platformPreloader;
	}

	private createPlatformPreloader(platform: Platform): IPlatformPreloader {
		switch (platform) {
			case 'vk':
				return new VkPlatformPreloader();
			case 'fbInstant':
				return new FbPlatformPreloader();
			case 'ya':
				return new YaPlatformPreloader();
			case 'cg':
				return new CrazyGamesPlatformPreloader();
		}

		return undefined;
	}

	private preventVConsole(): void {
		let removedNodesCount = 0;

		const nodeRemoveCallback = (node: ChildNode) => {
			node.remove();
			if (++removedNodesCount === 3) {
				observer.disconnect();
			}
		};

		const observer = new MutationObserver((mutations, observer) => {
			mutations.forEach((mut) => {
				mut.addedNodes.forEach((node) => {
					if (node instanceof Comment && node.textContent.includes('v-console')) {
						nodeRemoveCallback(node);
					}

					if (node instanceof HTMLScriptElement) {
						if (node.src.includes('vconsole') || node.innerText.includes('VConsole')) {
							nodeRemoveCallback(node);
						}
					}
				});
			});
		});

		observer.observe(document.body, { childList: true });
	}

	private onPreloaderClick(): void {
		if (++this.preloaderClicksNum >= 3) {
			this.preloaderClicksNum = 0;

			// на ФБ запрещены модальные окна (confirm, alert, etc.)
			const clearProfile = BUILD_CONFIG.platform === 'fbInstant' ? true : confirm('Очистить профиль?');
			if (clearProfile) {
				DEBUG_STORE.set('profile_clear_pending', '1');
				location.reload();
			}
		}
	}

	private addConsoleErrorHook(): void {
		const originalConsoleError = console.error.bind(console);
		console.error = (...args: any[]) => {
			'consoleErrorPreHook' in window && window.consoleErrorPreHook?.call(null, ...args);
			originalConsoleError(...args);
		};
	}

	private adjustScaling(): void {
		if (isDesktop()) {
			return;
		}

		// make preloader fill the whole page for mobile devices
		this.preloaderContainer.style.width = '100%';
		this.preloaderContainer.style.removeProperty('aspect-ratio');
	}

	private initBuildInfoText(buildConfig: BuildConfig): void {
		const buildInfo = document.getElementById('build-info');
		if (!buildInfo) {
			return;
		}

		const env = buildConfig.env;
		if (env === 'personal') {
			return;
		}

		const { buildNumber, buildDate } = buildConfig;
		const prefix = env === 'production' ? `v${buildNumber}` : `${env}-${buildNumber}`;
		buildInfo.innerText = `${prefix} (${buildDate})`;
	}

	public startFakeProgress(): void {
		if (typeof this.intervalId === 'number') {
			return;
		}

		this.intervalId = setInterval(this.fakeProgressUpdate.bind(this), 200);
	}

	public stopFakeProgress(): void {
		if (typeof this.intervalId === 'number') {
			clearInterval(this.intervalId);
			this.intervalId = undefined;
		}
	}

	private fakeProgressUpdate(): void {
		this.currentStageProgress += 1;
		this.updateTotalProgress(this.calculateTotalProgress());
	}

	public startStage(stage: LoadingStage): void {
		this.currentStage = stage;
		this.currentStageProgress = 0;
		this.updateTotalProgress(this.calculateTotalProgress());
	}

	private calculateTotalProgress(): number {
		const [start, end] = this.getStageSpan(this.currentStage);
		const adjustedProgress = Math.min(1, this.currentStageProgress / 100);
		return Math.round(start + adjustedProgress * (end - start));
	}

	private getStageSpan(stage: LoadingStage): [start: number, end: number] {
		const allStages = getObjectKeys(LOADING_STAGES);
		const stageIndex = allStages.indexOf(stage);
		const start = stageIndex === 0 ? 0 : LOADING_STAGES[allStages[stageIndex - 1]];
		const end = LOADING_STAGES[stage];
		return [start, end];
	}

	public updateTotalProgress(value: number): void {
		const progressMaxValue = this.isLoadComplete ? 100 : 99;
		this.totalProgress = clamp(Math.round(value), 0, progressMaxValue);
		this.loadingText.textContent = this.getLoadingTextContent(this.totalProgress);
		this.loadingBar.style.width = `${this.totalProgress}%`;
		this._platformPreloader?.setLoadingProgress(this.totalProgress);
	}

	private getLoadingTextContent(progress: number): string {
		const platform = BUILD_CONFIG.platform;
		if (platform === 'ya') {
			return progress + '%';
		}

		const prefix = platform === 'fbInstant' || platform === 'cg' ? 'Loading' : 'Загрузка';
		return `${prefix}: ${progress}%`;
	}

	public async complete(options: HideOptions) {
		this.stopFakeProgress();
		this.loadComplete.abort();
		this.isLoadComplete = true;

		if (options.instant === true) {
			this.updateTotalProgress(100);
			return Promise.resolve();
		}

		const start = this.totalProgress;
		const delta = 100 - start;
		return animate(
			(progress) => {
				this.updateTotalProgress(start + delta * progress);
			},
			{ duration: options.duration ?? 0.3 },
		).finished;
	}

	public async hide(options: HideOptions) {
		if (options.instant === true) {
			this.preloaderContainer.style.display = 'none';
			return;
		}

		try {
			// `animate()` may fail on old browsers, so we wrap it in try-catch
			animate(
				this.preloaderContainer,
				{ opacity: 0 },
				{
					duration: options.duration ?? 0.3,
					delay: 0.25,
					easing: 'ease-out',
				},
			).finished.finally(() => {
				this.preloaderContainer.style.display = 'none';
			});
		} catch (e) {
			this.preloaderContainer.style.display = 'none';
		}
	}

	public get isVisible(): boolean {
		return this.preloaderContainer.style.display !== 'none';
	}
}

// @ts-expect-error
window['preloader'] = new Preloader();
